import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { intersects, isEqual } from 'radash';
import {
  EMPTY,
  defer,
  Observable,
  Subject,
  catchError,
  exhaustMap,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { AppExceptionModel } from '../../models/app-exception.model';
import { GroupType } from '../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../models/member.school.model';
import { MembershipStatus } from '../../models/membership.status';
import { Pagination } from '../../models/pagination.model';
import { UserModel } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import { SchoolMemberService } from '../../services/member.school.service';
import * as groupActions from '../actions/group.actions';
import * as memberActions from '../actions/member.school.actions';
import * as paginationActions from '../actions/pagination.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';
import * as groupSelector from '../selectors/group.selector';
import * as memberSelector from '../selectors/member.school.selector';

@Injectable()
export class SchoolMemberEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  memberService: SchoolMemberService = inject(SchoolMemberService);
  toastr: ToastrService = inject(ToastrService);
  authService: AuthService = inject(AuthService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);
  location: Location = inject(Location);
  logger: NGXLogger = inject(NGXLogger);

  // user: UserModel | null = null;

  private logout$ = new Subject<void>();

  constructor() {
    // this.store
    //   .select((state) => state.user.user)
    //   .pipe(takeUntil(this.logout$))
    //   .subscribe((user) => {
    //     this.user = user;
    //   });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch Members
  getMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.getMembers),
      mergeMap(
        ({
          source,
          groupId,
          getMemberType,
          status,
          gradeId,
          academicYear,
          lastRecord,
          limit,
        }) => {
          // Prepare payload with only known properties
          const payload: any = {
            source,
            groupId,
            getMemberType,
            status,
            gradeId,
            academicYear,
            limit,
          };

          // Add lastRecord properties conditionally
          if (isEqual(getMemberType, [SchoolMemberType.parent])) {
            payload.lastRecord = lastRecord?.lastParentRecord;
          } else if (isEqual(getMemberType, [SchoolMemberType.student])) {
            payload.lastRecord = lastRecord?.lastStudentRecord;
          } else if (isEqual(getMemberType, [SchoolMemberType.unassigned])) {
            payload.lastRecord = lastRecord?.lastUnassignedRecord;
          } else if (
            intersects(getMemberType ?? [], [
              SchoolMemberType.teacher,
              SchoolMemberType.president,
              SchoolMemberType.principal,
            ])
          ) {
            payload.lastRecord = lastRecord?.lastFacultyRecord;
          }

          return defer(() => this.memberService.getMembers(payload)).pipe(
            mergeMap((members) =>
              this.resolveMembersPage(groupId, members, limit, getMemberType, lastRecord),
            ),
            // Per request, so one failure does not stop the effect.
            catchError((error) => {
              this.logger.error(`getMembers$ :: ${error}`);
              return of(memberActions.getMembersFailure({ error }));
            }),
          );
        },
      ),
    ),
  );

  private resolveMembersPage(
    groupId: string,
    members: SchoolMember[],
    limit: number | undefined,
    getMemberType: SchoolMemberType[] | undefined,
    lastRecord: Pagination | null | undefined,
  ): Observable<Action> {
    if (members.length === 0) {
      return of(memberActions.getMembersDone());
    }

    const finalMembers$ = members.map((member) =>
      this.afs
        .doc<UserModel>('users/' + member.memberId)
        .get()
        .pipe(
          map((snapshot) => {
            if (!snapshot.exists) return null;
            return Object.assign(
              {},
              snapshot.data(),
              member,
            ) as SchoolMember;
          }),
        ),
    );

    return forkJoin(finalMembers$).pipe(
      mergeMap((resolvedMembers) => {
        const validMembers = resolvedMembers.filter(
          (m): m is SchoolMember => m !== null,
        );

        return of(
          memberActions.addMembersToStore({
            members: validMembers.slice(0, limit ?? 10),
          }),
          paginationActions.updateAction({
            pagination: {
              id: groupId,
              ...lastRecord,
              ...(isEqual(getMemberType, [SchoolMemberType.parent])
                ? { lastParentRecord: validMembers[limit ?? 10] }
                : isEqual(getMemberType, [SchoolMemberType.student])
                  ? { lastStudentRecord: validMembers[limit ?? 10] }
                  : isEqual(getMemberType, [SchoolMemberType.unassigned])
                    ? { lastUnassignedRecord: validMembers[limit ?? 10] }
                    : intersects(getMemberType ?? [], [
                        SchoolMemberType.teacher,
                        SchoolMemberType.president,
                        SchoolMemberType.principal,
                      ])
                    ? { lastFacultyRecord: validMembers[limit ?? 10] }
                    : null),
            } as Pagination,
          }),
        );
      }),
    );
  }

  // Effect to fetch Members
  getMember$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.getMember),
      mergeMap(({ groupId, memberId }) =>
        defer(() => from(this.memberService.getMember(groupId, memberId))).pipe(
          mergeMap((member$) => member$.pipe(take(1))),
          mergeMap((memberDoc) => {
            if (memberDoc === null) {
              return of(
                memberActions.getMembersFailure({
                  error: {
                    code: 'ERR_NOT_FOUND',
                    title: 'Member not found',
                    message: 'The member you are looking for does not exist.',
                  } as AppExceptionModel,
                }),
              );
            }
            return this.afs
              .doc<UserModel>('users/' + memberDoc.memberId)
              .get()
              .pipe(
                switchMap((userSnapshot) => {
                  if (!userSnapshot.exists) return EMPTY;
                  const memberResult: SchoolMember = Object.assign(
                    {},
                    memberDoc,
                    userSnapshot.data(),
                  );
                  return of(
                    memberActions.addMemberToStore({ member: memberResult }),
                  );
                }),
              );
          }),
          catchError((error) => {
            this.logger.error(`getMember$ :: ${error}`);
            return of(memberActions.getMembersFailure({ error }));
          }),
        ),
      ),
    ),
  );

  loadFaculty$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      exhaustMap(([{ groupId }, group]) => {
        // console.log(`SchoolMemberEffects :: loadFaculty$: groupId: ${groupId}`);
        if (group == undefined || group?.groupType != GroupType.school)
          return EMPTY;
        if (!group?.statsLoaded) {
          // console.log(`SchoolMemberEffects :: loadFaculty$: Stats Loaded`);
          return of(
            memberActions.getMembers({
              source: 'SchoolMemberEffects :: loadFaculty$ : faculty',
              groupId: group?.id!,
              status: [MembershipStatus.active],
              getMemberType: [
                SchoolMemberType.president,
                SchoolMemberType.principal,
                SchoolMemberType.teacher,
              ],
              limit: 100,
            }),
          );
        } else {
          const teacherCount = group?.teachers ?? 0;

          // console.log(
          //   `SchoolMemberEffects :: loadFaculty$: Get members of type teacher`
          // );
          return this.store
            .select(
              memberSelector.getGroupMembersByType({
                memberType: [SchoolMemberType.teacher],
                status: [MembershipStatus.active],
                groupId: group?.id!,
              }),
            )
            .pipe(
              take(1),
              switchMap((teachers) => {
                if (teachers.length < teacherCount || teachers.length == 0) {
                  // console.log(
                  //   `SchoolMemberEffects :: loadFaculty$: Get teachers`
                  // );
                  return of(
                    memberActions.getMembers({
                      source: 'SchoolMemberEffects :: loadFaculty$ : teachers',
                      groupId: group?.id!,
                      status: [MembershipStatus.active],
                      getMemberType: [SchoolMemberType.teacher],
                      limit: 100,
                    }),
                  );
                } else {
                  return EMPTY;
                }
              }),
              catchError((error) => {
                return of(memberActions.getMembersFailure({ error }));
              }),
            );
        }
      }),
      catchError((error) => {
        return of(memberActions.getMembersFailure({ error }));
      }),
    ),
  );

  loadParents$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        if (group == undefined || group?.groupType != GroupType.school)
          return EMPTY;
        return this.store.select(groupSelector.getMyRolesInGroup(groupId)).pipe(
          take(1),
          switchMap((roles) => {
            if (!roles.management) return EMPTY;

            return this.store
              .select(
                memberSelector.getGroupMembersByType({
                  memberType: [SchoolMemberType.parent],
                  status: [MembershipStatus.active],
                  groupId: group?.id!,
                }),
              )
              .pipe(
                take(1),
                switchMap((parents) => {
                  if (parents.length == 0) {
                    return of(
                      memberActions.getMembers({
                        source: 'SchoolMemberEffects :: loadParents$',
                        groupId: group?.id!,
                        status: [MembershipStatus.active],
                        getMemberType: [SchoolMemberType.parent],
                      }),
                    );
                  } else {
                    return EMPTY;
                  }
                }),
                catchError((error) => {
                  return of(memberActions.getMembersFailure({ error }));
                }),
              );
          }),
        );
      }),
      catchError((error) => {
        return of(memberActions.getMembersFailure({ error }));
      }),
    ),
  );

  loadStudents$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        if (group == undefined || group?.groupType != GroupType.school)
          return EMPTY;
        return this.store.select(groupSelector.getMyRolesInGroup(groupId)).pipe(
          take(1),
          switchMap((roles) => {
            if (!roles.management) return EMPTY;

            return this.store
              .select(
                memberSelector.getGroupMembersByType({
                  memberType: [SchoolMemberType.student],
                  groupId: group?.id!,
                  status: [MembershipStatus.active],
                }),
              )
              .pipe(
                take(1),
                switchMap((students) => {
                  if (students.length == 0) {
                    return of(
                      memberActions.getMembers({
                        source: 'SchoolMemberEffects :: loadStudents$',
                        groupId: group?.id!,
                        status: [MembershipStatus.active],
                        getMemberType: [SchoolMemberType.student],
                      }),
                    );
                  } else {
                    return EMPTY;
                  }
                }),
                catchError((error) => {
                  return of(memberActions.getMembersFailure({ error }));
                }),
              );
          }),
        );
      }),
      catchError((error) => {
        return of(memberActions.getMembersFailure({ error }));
      }),
    ),
  );

  // Effect to update a member
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.update),
      switchMap(({ id, currMember, member }) => {
        const uid = this.authService.getUid();

        if (!uid) {
          this.toastr.error(
            `User not logged in. Not authorized to update member.`,
            'Error Updating Member',
          );
          return EMPTY;
        }

        const updateMember: SchoolMember = {
          ...member,
          approvedById: uid,
          modified: serverTimestamp() as Timestamp,
        };

        this.logger.debug(
          `update$ :: Updating School Member: ${JSON.stringify(updateMember)}`,
        );

        return from(this.memberService.update(id, updateMember)).pipe(
          map(() => {
            const localMember: SchoolMember = {
              ...currMember,
              ...member,
              id: id,
              // Server sets the real time; keep a close local value.
              modified: Timestamp.now(),
            };

            this.toastr.success(
              'The member details have been updated successfully.',
              'Member Updated',
            );
            return memberActions.updateSuccess({
              currMember: currMember,
              member: localMember,
            });
          }),
          catchError((error) => {
            console.error(`Error updating member: ${error.toString()}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Updating Member',
            );
            return of(memberActions.updateFailure({ error }));
          }),
        );
      }),
    ),
  );

  // Effect to update a member status
  updateStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.updateStatus),
      switchMap(({ id, member }) =>
        defer(() =>
          this.memberService.update(id, {
            ...member,
            modified: serverTimestamp() as Timestamp,
          }),
        ).pipe(
          map(() =>
            memberActions.updateStatusSuccess({
              member: { ...member, id: id, modified: Timestamp.now() },
            }),
          ),
          // Per request, so one failed update does not stop the effect.
          catchError((error) => {
            this.logger.error(`updateStatus$ :: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Updating Member Status',
            );
            return of(memberActions.updateStatusFailure({ error }));
          }),
        ),
      ),
    ),
  );

  linkParent$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.linkParent),
      switchMap(async ({ student, parent }) => {
        try {
          await this.memberService.linkParent(student, parent);
          this.toastr.success(
            'The parent has been linked successfully.',
            'Parent Linked',
          );
          return memberActions.linkParentSuccess({ student, parent });
        } catch (error) {
          console.error(`Error linking parent: ${error}`);
          this.toastr.error(
            `An error occurred, '${error}'. Please try again later.`,
            'Error Linking Parent',
          );
          const exception: AppExceptionModel = {
            code: 'ERR_LINK_PARENT',
            title: 'Link Parent Error',
            message: 'An error occurred while linking parent.',
          };
          return memberActions.linkParentFailure({ error: exception });
        }
      }),
    ),
  );

  delinkParent$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.delinkChild),
      switchMap(async ({ student, parent }) => {
        try {
          await this.memberService.delinkParent(student, parent);
          this.toastr.success(
            'The child has been delinked successfully.',
            'Child Delinked',
          );

          let storeStudent: SchoolMember | null | undefined;
          let storeParent: SchoolMember | null | undefined;
          this.store
            .select(
              memberSelector.getSchoolMemberById(
                student.memberId,
                student.groupId,
              ),
            )
            .pipe(take(1))
            .subscribe((s) => {
              storeStudent = s;
            });
          this.store
            .select(
              memberSelector.getSchoolMemberById(
                parent.memberId,
                parent.groupId,
              ),
            )
            .pipe(take(1))
            .subscribe((p) => {
              storeParent = p;
            });

          return memberActions.delinkChildSuccess({
            student: {
              ...storeStudent,
              parentId: undefined,
              parentName: undefined,
              modified: Timestamp.now(),
            } as SchoolMember,
            parent: {
              ...storeParent,
              studentId: parent.studentId,
              modified: Timestamp.now(),
            } as SchoolMember,
          });
        } catch (error) {
          // console.error(`Error delinking child: ${error}`);
          this.toastr.error(
            `An error occurred, '${error}'. Please try again later.`,
            'Error Delinking Child',
          );
          const exception: AppExceptionModel = {
            code: 'ERR_DELINK_CHILD',
            title: 'Delink Child Error',
            message: 'An error occurred while delinking child.',
          };
          return memberActions.delinkChildFailure({ error: exception });
        }
      }),
    ),
  );

  // effect to delete a member
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.deleteAction),
      switchMap(({ id }) => {
        const parts = id.split('_');
        const groupId = parts[0];
        const memberId = parts.slice(1).join('_');

        return this.memberService
          .delete(id)
          .then(() => {
             this.toastr.success(
              'The member has been removed successfully.',
              'Member Removed',
            );
            return memberActions.deleteSuccess({ id: id });
          })
          .catch((error) => {
            console.error(`Error deleting member: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Removing Member',
            );
            return memberActions.deleteFailure({ error });
          });
      }),
    ),
  );

  // Get New Members who joined in the last 30 days
  //   getMemberStatistics$: Observable<Action> = createEffect(() =>
  //     this.actions$.pipe(
  //       ofType(
  //         memberActions.getMemberStats,
  //         memberActions.createSuccess,
  //         memberActions.updateStatusSuccess
  //       ),
  //       mergeMap((action) => {
  //         let groupId = '';
  //         if (action.type === memberActions.getMemberStats.type) {
  //           groupId = action.groupId;
  //         } else if (action.type === memberActions.createSuccess.type) {
  //           groupId = action.member.groupId;
  //         } else if (action.type === memberActions.updateStatusSuccess.type) {
  //           groupId = action.member.groupId;
  //         }

  //         return from(this.memberService.getMemberStats(groupId)).pipe(
  //           switchMap((snapshot) => {
  //             let g: any = {
  //               id: groupId,
  //               newMembers: snapshot?.count ?? '-',
  //             };
  //             return of(memberActions.getMemberStatsDone({ group: g }));
  //           }),
  //           catchError((error) =>
  //             of(memberActions.getMemberStatsFailure({ error }))
  //           )
  //         );
  //       })
  //     )
  //   );

  clearGroupMembers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.clearFromStore),
      switchMap(({ id }) =>
        this.store.select(memberSelector.getSchoolMembersByGroupId(id)).pipe(
          take(1),
          switchMap((groupMembers) => {
            // Entities are keyed by membership doc id, not by user id.
            const members = groupMembers
              .map((m) => m.id)
              .filter((m): m is string => !!m);
            if (members.length > 0) {
              return of(memberActions.clearFromStore({ ids: members }));
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  clearMembers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(memberActions.clear());
      }),
    ),
  );
}
